import React, { useState } from "react";
import {
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaRandom,
  FaCrown,
  FaCalendarAlt,
} from "react-icons/fa";
import { createEmailUser } from "../services/emailAuthService";
import { checkDuplicateEmail } from "../utils/emailUserUtils";
import { generateSecurePassword } from "../utils/passwordUtils";
import { getDatabase, ref, set, get } from "firebase/database";
import { app } from "../firebase/config";
import { hashPassword } from "../utils/passwordUtils";

// Custom function to create email user with custom expiry date
const createEmailUserWithCustomExpiry = async (
  email,
  password,
  displayName,
  adminId,
  membership = null,
  customExpiryTimestamp = null
) => {
  try {
    console.log("🔄 Creating email user with custom expiry:", email);

    if (!email || !password || !adminId) {
      return {
        success: false,
        error: "Email, password, and admin ID are required",
        userId: null,
      };
    }

    // Use email as display name if not provided
    const finalDisplayName = displayName || email.split("@")[0];

    // Check for duplicate email
    const isDuplicate = await checkEmailExists(email);
    if (isDuplicate) {
      return {
        success: false,
        error: "Email already exists",
        userId: null,
      };
    }

    // Generate unique user ID
    const userId = generateEmailUserId();

    // Hash password
    const passwordHash = await hashPassword(password);

    // Use custom expiry timestamp if provided, otherwise calculate default
    let membershipExpiry = customExpiryTimestamp;
    if (membership && !customExpiryTimestamp) {
      const expiryDate = new Date();
      switch (membership) {
        case 1: // 7 days
          expiryDate.setDate(expiryDate.getDate() + 7);
          break;
        case 2: // 30 days
          expiryDate.setDate(expiryDate.getDate() + 30);
          break;
        case 3: // 180 days
          expiryDate.setDate(expiryDate.getDate() + 180);
          break;
        case 4: // 360 days
          expiryDate.setDate(expiryDate.getDate() + 360);
          break;
        default:
          expiryDate.setDate(expiryDate.getDate() + 30);
      }

      // Round up to next hour
      expiryDate.setMinutes(0);
      expiryDate.setSeconds(0);
      expiryDate.setMilliseconds(0);
      expiryDate.setHours(expiryDate.getHours() + 1);

      membershipExpiry = expiryDate.getTime();
    }

    // Prepare user data
    const userData = {
      userID: userId,
      createdAt: Date.now(),
      dailyPostCount: {},
      profile: {
        displayName: finalDisplayName,
        email: email,
        photoURL: null,
        authProvider: "email",
      },
      emailAuth: {
        email: email,
        passwordHash: passwordHash,
        isActive: true,
        lastLogin: null,
        createdBy: adminId,
        createdAt: Date.now(),
      },
    };

    // Add membership if provided
    if (membership && membershipExpiry) {
      userData.membership = membership;
      userData.membershipExpiry = membershipExpiry;
    }

    // Save user to database
    const database = getDatabase(app);
    const userRef = ref(database, `users/${userId}`);
    await set(userRef, userData);

    // OPTIMIZED: Also save to user_profiles
    try {
      const userProfileData = {
        id: userId,
        createdAt: userData.createdAt,
        profile: userData.profile,
        emailAuth: userData.emailAuth,
        membership: userData.membership || null,
        membershipExpiry: userData.membershipExpiry || null,
        stats: { postCount: 0, createdPostCount: 0 }
      };
      await set(ref(database, `user_profiles/${userId}`), userProfileData);
    } catch (e) {
      console.error("Error creating user_profile:", e);
    }

    console.log(
      "✅ Email user created successfully with custom expiry:",
      userId
    );

    return {
      success: true,
      error: null,
      userId: userId,
      user: userData,
    };
  } catch (error) {
    console.error("❌ Error creating email user with custom expiry:", error);
    return {
      success: false,
      error: "Failed to create user. Please try again.",
      userId: null,
    };
  }
};

// Helper functions
const checkEmailExists = async (email) => {
  try {
    const database = getDatabase(app);
    // OPTIMIZED: Use user_profiles instead of users
    const usersRef = ref(database, "user_profiles");
    const snapshot = await get(usersRef);

    if (!snapshot.exists()) {
      return false;
    }

    const users = snapshot.val();

    // Check in both Google users and email users
    for (const userData of Object.values(users)) {
      // Check Google users
      if (userData.profile?.email === email) {
        return true;
      }
      // Check email auth users
      if (userData.emailAuth?.email === email) {
        return true;
      }
    }

    return false;
  } catch (error) {
    console.error("❌ Error checking email existence:", error);
    return false;
  }
};

const generateEmailUserId = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `email_${timestamp}_${randomStr}`;
};

const AdminEmailUserCreation = ({ onUserCreated, onCancel, adminId }) => {
  // Get default end date for initial membership selection
  const getInitialEndDate = () => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 7); // Default 7 days for membership "1"
    return endDate.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState({
    email: "",
    password: "123456",
    membership: "1",
    membershipEndDate: getInitialEndDate(),
    generatePassword: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");

  // Membership options
  const membershipOptions = [
    { value: "", label: "Miễn phí (Không có gói thành viên)" },
    { value: 1, label: "7 ngày" },
    { value: 2, label: "30 ngày" },
    { value: 3, label: "180 ngày" },
    { value: 4, label: "360 ngày" },
  ];

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear general error
    if (errors.general) {
      setErrors((prev) => ({
        ...prev,
        general: "",
      }));
    }
  };

  // Get default end date based on membership selection
  const getDefaultEndDate = (membershipType) => {
    if (!membershipType) return "";

    const today = new Date();
    let endDate = new Date(today);

    switch (parseInt(membershipType)) {
      case 1: // 7 days
        endDate.setDate(today.getDate() + 7);
        break;
      case 2: // 30 days
        endDate.setDate(today.getDate() + 30);
        break;
      case 3: // 180 days
        endDate.setDate(today.getDate() + 180);
        break;
      case 4: // 360 days
        endDate.setDate(today.getDate() + 360);
        break;
      default:
        return "";
    }

    // Format as YYYY-MM-DD for date input
    return endDate.toISOString().split("T")[0];
  };

  // Handle membership change and auto-set end date
  const handleMembershipChange = (e) => {
    const membershipValue = e.target.value;
    const defaultEndDate = getDefaultEndDate(membershipValue);

    setFormData((prev) => ({
      ...prev,
      membership: membershipValue,
      membershipEndDate: defaultEndDate,
    }));

    // Clear errors
    if (errors.membership) {
      setErrors((prev) => ({
        ...prev,
        membership: "",
      }));
    }
    if (errors.membershipEndDate) {
      setErrors((prev) => ({
        ...prev,
        membershipEndDate: "",
      }));
    }
  };

  // Generate random password
  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword(8);
    setFormData((prev) => ({
      ...prev,
      password: newPassword,
      generatePassword: true,
    }));

    setShowPassword(true);
  };

  // Validate form
  const validateForm = async () => {
    const newErrors = {};

    // Validate email
    if (!formData.email) {
      newErrors.email = "Email là bắt buộc";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Định dạng email không hợp lệ";
      }
    }

    // Validate password (minimum 6 characters)
    if (!formData.password) {
      newErrors.password = "Mật khẩu là bắt buộc";
    } else if (formData.password.length < 6) {
      newErrors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    // Validate membership end date if membership is selected
    if (formData.membership && formData.membership !== "") {
      if (!formData.membershipEndDate) {
        newErrors.membershipEndDate =
          "Ngày kết thúc membership là bắt buộc khi chọn gói thành viên";
      } else {
        const endDate = new Date(formData.membershipEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

        if (endDate <= today) {
          newErrors.membershipEndDate = "Ngày kết thúc phải sau ngày hôm nay";
        }
      }
    }

    // Validate admin ID
    if (!adminId) {
      newErrors.general = "ID quản trị viên là bắt buộc";
    }

    // Check for duplicate email
    if (formData.email && !newErrors.email) {
      const duplicateCheck = await checkDuplicateEmail(formData.email);
      if (duplicateCheck.isDuplicate) {
        newErrors.email = duplicateCheck.error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setErrors({});
    setSuccessMessage("");

    // Validate form
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      // Calculate custom expiry date if provided
      let customExpiryTimestamp = null;
      if (formData.membership && formData.membershipEndDate) {
        const endDate = new Date(formData.membershipEndDate);
        // Set to end of day (23:59:59)
        endDate.setHours(23, 59, 59, 999);
        customExpiryTimestamp = endDate.getTime();
      }

      // Create user
      const result = await createEmailUserWithCustomExpiry(
        formData.email,
        formData.password,
        null, // No display name
        adminId,
        formData.membership ? parseInt(formData.membership) : null,
        customExpiryTimestamp
      );

      if (result.success) {
        setSuccessMessage(
          `Tạo người dùng thành công! ID người dùng: ${result.userId}`
        );

        // Reset form
        setFormData({
          email: "",
          password: "123456",
          membership: "1",
          membershipEndDate: "",
          generatePassword: false,
        });

        // Notify parent component
        if (onUserCreated) {
          onUserCreated(result.user);
        }

        // Auto close after 2 seconds
        setTimeout(() => {
          if (onCancel) {
            onCancel();
          }
        }, 2000);
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setErrors({
        general: "Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Tạo Người Dùng Email Mới
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Tạo tài khoản người dùng mới với xác thực email và mật khẩu
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
              {successMessage}
            </div>
          )}

          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          {/* Email Input */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Địa Chỉ Email *
            </label>
            <div className="relative">
              <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="nguoidung@example.com"
                disabled={isLoading}
                required
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Password Input */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                Mật Khẩu *
              </label>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                disabled={isLoading}
              >
                <FaRandom className="mr-1" />
                Tạo Ngẫu Nhiên
              </button>
            </div>
            <div className="relative">
              <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.password ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Nhập mật khẩu"
                disabled={isLoading}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isLoading}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Password Length Indicator */}
            {formData.password && (
              <div className="mt-2">
                <p className="text-xs text-gray-600">
                  Độ dài mật khẩu: {formData.password.length} ký tự
                  {formData.password.length < 6 && (
                    <span className="text-red-500 ml-1">
                      (tối thiểu 6 ký tự)
                    </span>
                  )}
                </p>
              </div>
            )}

            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          {/* Membership Selection */}
          <div>
            <label
              htmlFor="membership"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Cấp Độ Thành Viên
            </label>
            <div className="relative">
              <FaCrown className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                id="membership"
                name="membership"
                value={formData.membership}
                onChange={handleMembershipChange}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                {membershipOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Chọn cấp độ thành viên cho người dùng mới (tùy chọn)
            </p>
          </div>

          {/* Membership End Date */}
          {formData.membership && formData.membership !== "" && (
            <div>
              <label
                htmlFor="membershipEndDate"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Ngày Kết Thúc Membership *
              </label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  id="membershipEndDate"
                  name="membershipEndDate"
                  value={formData.membershipEndDate}
                  onChange={handleInputChange}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.membershipEndDate
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  disabled={isLoading}
                  min={new Date().toISOString().split("T")[0]} // Minimum date is today
                  required
                />
              </div>
              {errors.membershipEndDate ? (
                <p className="mt-1 text-sm text-red-600">
                  {errors.membershipEndDate}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  Membership sẽ hết hạn vào cuối ngày đã chọn (23:59:59)
                </p>
              )}
              {formData.membershipEndDate && (
                <div className="mt-1">
                  <p className="text-xs text-blue-600">
                    Ngày hết hạn:{" "}
                    {new Date(formData.membershipEndDate).toLocaleDateString(
                      "vi-VN",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      }
                    )}{" "}
                    lúc 23:59:59
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={isLoading}
            >
              Hủy
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? "Đang tạo..." : "Tạo Người Dùng"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminEmailUserCreation;
